/**
 * Trust Requests — Admin review queue for NDA requests and security questionnaires.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Shield, Mail, Copy, Check, X as XIcon, Clock, FileText, ExternalLink } from 'lucide-react';
import { Card } from '../components/ui-custom/ComplianceUI';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const NDA_STATUSES = [
  { value: 'pending', label: 'Pending', color: '#f59e0b' },
  { value: 'approved', label: 'Approved', color: '#10b981' },
  { value: 'rejected', label: 'Rejected', color: '#dc2626' },
  { value: 'completed', label: 'Completed', color: '#FF00BE' },
];
const Q_STATUSES = [
  { value: 'received', label: 'Received', color: '#f59e0b' },
  { value: 'in_progress', label: 'In Progress', color: '#FF00BE' },
  { value: 'completed', label: 'Completed', color: '#10b981' },
  { value: 'declined', label: 'Declined', color: '#dc2626' },
];

export default function TrustRequestsPage() {
  const [data, setData] = useState({ nda_requests: [], questionnaires: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('nda');
  const [savingId, setSavingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/trust-center/requests`, { credentials: 'include' });
      if (!res.ok) { toast.error("Failed to load requests"); return; }
      const d = await res.json();
      setData(d);
    } catch (e) {
      console.error(e);
      toast.error('Load error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const updateStatus = async (kind, id, status) => {
    setSavingId(id);
    try {
      const res = await fetch(`${API_URL}/api/trust-center/requests/${kind}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) { toast.error('Update failed'); return; }
      toast.success(`Marked as ${status}`);
      await fetchRequests();
    } finally {
      setSavingId(null);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  const stats = useMemo(() => ({
    ndaPending: data.nda_requests.filter(r => r.status === 'pending').length,
    ndaTotal: data.nda_requests.length,
    qReceived: data.questionnaires.filter(r => ['received', 'in_progress'].includes(r.status)).length,
    qTotal: data.questionnaires.length,
  }), [data]);

  const list = tab === 'nda' ? data.nda_requests : data.questionnaires;
  const statuses = tab === 'nda' ? NDA_STATUSES : Q_STATUSES;

  const renderBadge = (status) => {
    const s = statuses.find(x => x.value === status);
    const c = s?.color || '#6b7280';
    return (
      <span style={{
        background: `${c}22`, color: c, border: `1px solid ${c}55`,
        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {s?.label || status}
      </span>
    );
  };

  return (
    <div className="trust-requests-page" data-testid="trust-requests-page">
      <div className="page-header">
        <div>
          <h1>Trust Requests</h1>
          <p>
            NDA requests and security questionnaires submitted through the public
            Trust Center. You (and every other Owner/Admin) are emailed instantly
            when a new one arrives — respond within your SLA.
          </p>
        </div>
        <div className="user-stats">
          <div className="user-stat"><span className="stat-value">{stats.ndaPending}</span><span className="stat-label">NDA Pending</span></div>
          <div className="user-stat"><span className="stat-value">{stats.qReceived}</span><span className="stat-label">Q. Open</span></div>
          <div className="user-stat"><span className="stat-value">{stats.ndaTotal + stats.qTotal}</span><span className="stat-label">All Time</span></div>
        </div>
      </div>

      <div className="tr-tabs">
        <button
          className={`tr-tab ${tab === 'nda' ? 'active' : ''}`}
          onClick={() => setTab('nda')}
          data-testid="tab-nda"
        >
          <Shield size={16} /> NDA Requests
          {stats.ndaPending > 0 && <span className="tab-count">{stats.ndaPending}</span>}
        </button>
        <button
          className={`tr-tab ${tab === 'questionnaire' ? 'active' : ''}`}
          onClick={() => setTab('questionnaire')}
          data-testid="tab-questionnaire"
        >
          <FileText size={16} /> Security Questionnaires
          {stats.qReceived > 0 && <span className="tab-count">{stats.qReceived}</span>}
        </button>
      </div>

      {loading ? (
        <div className="users-loading">Loading…</div>
      ) : list.length === 0 ? (
        <Card className="empty-state" style={{ padding: 48, textAlign: 'center' }}>
          <Clock size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p>No {tab === 'nda' ? 'NDA requests' : 'questionnaires'} yet. The public Trust Center page forwards new submissions here automatically.</p>
        </Card>
      ) : (
        <div className="tr-list">
          {list.map((r) => (
            <Card key={r.id} className="tr-card" data-testid={`tr-card-${r.id}`}>
              <div className="tr-card-head">
                <div>
                  <div className="tr-company">{r.company_name}</div>
                  <div className="tr-contact">
                    {r.contact_name} &middot;{' '}
                    <a href={`mailto:${r.contact_email}`} className="tr-link">{r.contact_email}</a>
                    <button
                      className="tr-copy-btn"
                      onClick={() => copy(r.contact_email)}
                      title="Copy email"
                      data-testid={`copy-${r.id}`}
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                  <div className="tr-meta">
                    Submitted {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="tr-status">{renderBadge(r.status)}</div>
              </div>

              {(r.reason || r.notes) && (
                <div className="tr-body">
                  <div className="tr-body-label">{tab === 'nda' ? 'Reason' : 'Notes'}</div>
                  <div className="tr-body-text">{r.reason || r.notes}</div>
                </div>
              )}

              {r.file_name && (
                <div className="tr-body">
                  <div className="tr-body-label">Attached file</div>
                  <div className="tr-body-text">
                    {r.file_name}
                    {r.file_size ? ` (${Math.round(r.file_size / 1024)} KB)` : ''}
                  </div>
                </div>
              )}

              {tab === 'nda' && r.approval_email_sent && (
                <div className="tr-body" data-testid={`email-sent-${r.id}`}>
                  <div className="tr-body-label">Signable NDA sent</div>
                  <div className="tr-body-text" style={{ color: '#10b981', fontWeight: 500 }}>
                    <Check size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Emailed to {r.contact_email}
                    {r.approval_email_sent_at
                      ? ` on ${new Date(r.approval_email_sent_at).toLocaleString()}`
                      : ''}
                  </div>
                </div>
              )}

              <div className="tr-actions">
                <span className="tr-actions-label">Update status:</span>
                {statuses.map(s => (
                  <button
                    key={s.value}
                    className={`tr-action-btn ${r.status === s.value ? 'current' : ''}`}
                    style={r.status === s.value ? { background: s.color, borderColor: s.color, color: '#fff' } : {}}
                    onClick={() => updateStatus(tab, r.id, s.value)}
                    disabled={savingId === r.id || r.status === s.value}
                    data-testid={`status-${s.value}-${r.id}`}
                  >
                    {s.label}
                  </button>
                ))}
                <a
                  href={`mailto:${r.contact_email}?subject=Re: ${tab === 'nda' ? 'NDA Request' : 'Security Questionnaire'} — ${r.company_name}`}
                  className="tr-action-btn"
                  style={{ marginLeft: 'auto' }}
                >
                  <Mail size={12} /> Reply
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
